/**
 * Shared Delegation Hooks
 *
 * Cross-feature delegation awareness (SAP HRBAS00 substitution).
 * Used by both HRMS Leave and Attendance modules.
 * React Query caches the delegation data — multiple consumers share one cache entry.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { DelegationListResponse } from '@/features/hrms/leaves/types';

export const delegationKeys = {
  all: ['delegations'] as const,
  list: () => [...delegationKeys.all, 'list'] as const,
};

async function fetchDelegations(): Promise<DelegationListResponse> {
  return apiClient.get<DelegationListResponse>('/hrms/leave/delegations');
}

export function useDelegations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: delegationKeys.list(),
    queryFn: fetchDelegations,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Check if current user has active incoming delegation for a specific entity type.
 * Always pass entityType to prevent cross-module false positives.
 */
export function useHasActiveDelegation(
  entityType?: string,
  options?: { enabled?: boolean },
): boolean {
  const { data } = useDelegations(options);
  const delegatedTo = data?.delegatedTo ?? [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return delegatedTo.some((d: { isActive: boolean; endDate: string; entityTypes: string[] }) => {
    if (!d.isActive) return false;
    if (new Date(d.endDate) < now) return false;
    if (entityType) {
      if (d.entityTypes.length > 0 && !d.entityTypes.includes(entityType)) return false;
    }
    return true;
  });
}
